import { type FormikValues } from "formik";
import { useCallback, useEffect, useRef, useState } from "react";

import type {
  ActionMeta,
  GroupBase,
  OptionsOrGroups,
  SingleValue,
} from "react-select";
import { AsyncPaginate } from "react-select-async-paginate";
import { apiRequest } from "shared/api/apiRequest";
import { type SurveyModel } from "survey-core";
import * as Yup from "yup";
import { profile_type } from "prisma-generated/enums";
import { getSocietyDirectoryIdContract } from "shared/api/contracts/society/societyId/directory/directoryId";
import { postSocietyWorkflowInstanceContract } from "shared/api/contracts/society/societyId/workflow/workflowId/instance";
import CrossIcon from "shared/components/icons/sign/CrossIcon";
import PillarForm from "shared/components/pillar-form/PillarForm";
import {
  ProfileSearchMultipleQuestionProperties,
  ProfileSearchMultipleQuestionType,
} from "shared/components/survey-js-form/questions/profile-search/profileSearchConstants";
import {
  type ProfileSearchMultipleQuestionData,
  type ProfileSearchQuestionData,
  type ProfileSearchQuestionFilterOption,
} from "shared/components/survey-js-form/questions/profile-search/ProfileSearchQuestionTypes";
import { defaultEmailFromProfile } from "shared/helpers/profile/defaultEmailFromProfile";
import { getDisplayNameHelper } from "shared/helpers/profile/getDisplayNameHelper";
import { type SessionView } from "shared/mappers/database/session/session";

const createNewProfileOption: OptionsValuesSurveyJsProfileSearch = {
  value: {
    profileId: -1,
  },
  label: "Create New Profile",
};

export type SurveyJSProfileSearchProps = {
  directoryId: number;
  workflowId: number;
  emailRequired?: boolean;
  affiliationRequired?: boolean;
  firstNameRequired?: boolean;
  lastNameRequired?: boolean;
  session: SessionView | null;
  baseUrl?: string;
  existingValue?: ProfileSearchQuestionData;
  handleChanged: (values: FormikValues) => void;
  overrideAffiliation?: boolean;
  allowChangeProfile?: boolean;
  survey?: SurveyModel;
  allowInlineParticipantEntry?: boolean;
  filterOptions?: ProfileSearchQuestionFilterOption[];
  panelIndex?: number;
};

export type OptionsValuesSurveyJsProfileSearch = {
  value: ProfileSearchQuestionData;
  label: string;
};

const SurveyJSProfileSearch = ({
  directoryId,
  workflowId,
  emailRequired,
  affiliationRequired,
  firstNameRequired,
  lastNameRequired,
  session,
  existingValue,
  allowInlineParticipantEntry,
  handleChanged,
  overrideAffiliation,
  allowChangeProfile,
  survey,
  filterOptions,
  panelIndex,
}: SurveyJSProfileSearchProps) => {
  const [createProfile, setCreateProfile] = useState(false);
  const [newValue, setNewValue] =
    useState<OptionsValuesSurveyJsProfileSearch | null>(null);
  const preservedWorkflowInstanceIdRef = useRef<number | undefined>(undefined);
  const [overrideAffiliationValue, setOverrideAffiliationValue] =
    useState<string>(existingValue?.affiliation ?? "");
  const [hasExistingProfile, setHasExistingProfile] = useState(false);
  const [isLocked, setIsLocked] = useState(false);

  const [apiFilters, setApiFilters] = useState<Record<string, any>>({});
  const [selectKey, setSelectKey] = useState(0); // Key to force AsyncPaginate reload

  const validationSchema = Yup.object({
    firstName: firstNameRequired
      ? Yup.string().required("First name is required")
      : Yup.string(),
    lastName: lastNameRequired
      ? Yup.string().required("Last name is required")
      : Yup.string(),
    affiliation: affiliationRequired
      ? Yup.string().required("Affiliation is required")
      : Yup.string(),
    email: emailRequired
      ? Yup.string().email().required("Email is required")
      : Yup.string().email(),
  });

  useEffect(() => {
    if (existingValue && !newValue) {
      preservedWorkflowInstanceIdRef.current = existingValue.workflowInstanceId;
      setNewValue({
        value: {
          profileId: existingValue.profileId,
          displayName: existingValue.displayName,
          affiliation: existingValue.affiliation,
          workflowInstanceId: existingValue.workflowInstanceId,
        },
        label: existingValue.displayName!,
      });
      setIsLocked(true);
    }
  }, [existingValue, newValue]);

  // useEffect to watch other questions based on filterOptions
  useEffect(() => {
    if (!survey) {
      return;
    }

    const updateFiltersAndKey = () => {
      if (!filterOptions || filterOptions.length === 0) {
        if (Object.keys(apiFilters).length > 0) {
          setApiFilters({});
          setSelectKey((prevKey) => prevKey + 1);
        }
        return;
      }

      const newApiFiltersFromSurvey: Record<string, any> = {};
      filterOptions.forEach((opt) => {
        const watchedQuestionValue = survey.getValue(opt.questionName);

        if (
          watchedQuestionValue !== undefined &&
          watchedQuestionValue !== null
        ) {
          let valueForApiFilter;

          if (
            typeof watchedQuestionValue === "string" &&
            watchedQuestionValue !== ""
          ) {
            valueForApiFilter = watchedQuestionValue;
          }
          // Check if the watched value is an object and if the filterKey exists as a property in it
          else if (
            typeof watchedQuestionValue === "object" &&
            opt.filterKey in watchedQuestionValue
          ) {
            valueForApiFilter = (watchedQuestionValue as Record<string, any>)[
              opt.filterKey
            ];
          } else if (typeof watchedQuestionValue === "object") {
            console.warn(
              `SurveyJSProfileSearch: Watched question '${opt.questionName}' returned an object, but the key '${opt.filterKey}' was not found in the object. Skipping this filter.`,
            );
            return;
          } else {
            console.warn(
              `SurveyJSProfileSearch: Watched question '${opt.questionName}' provided a non-object value. The filterKey '${opt.filterKey}' expects an object to extract a property from. Skipping this filter.`,
            );
            return;
          }

          // Add the extracted value to the API filters if it's valid
          if (
            valueForApiFilter !== undefined &&
            valueForApiFilter !== null &&
            String(valueForApiFilter).trim() !== ""
          ) {
            newApiFiltersFromSurvey[opt.filterKey] = valueForApiFilter;
          }
        }
      });

      if (
        JSON.stringify(newApiFiltersFromSurvey) !== JSON.stringify(apiFilters)
      ) {
        setApiFilters(newApiFiltersFromSurvey);
        setSelectKey((prevKey) => prevKey + 1);
      }
    };

    updateFiltersAndKey();

    const valueChangedCallback = (sender: SurveyModel, options: any) => {
      const questionType = options?.question?.getType?.();
      if (questionType === ProfileSearchMultipleQuestionType) {
        setSelectKey((prev) => prev + 1);
      }

      // Check if the changed question is one of the watched questions
      const changedQuestionName = options?.name;
      if (
        filterOptions &&
        changedQuestionName &&
        filterOptions.some((opt) => opt.questionName === changedQuestionName)
      ) {
        updateFiltersAndKey();
      }
    };

    survey.onValueChanged.add(valueChangedCallback);

    return () => {
      survey.onValueChanged.remove(valueChangedCallback);
    };
  }, [survey, filterOptions, apiFilters]);

  const handleCreateProfile = async (values: FormikValues) => {
    const response = await apiRequest({
      contract: postSocietyWorkflowInstanceContract,
      inputs: {
        params: {
          societyId: session?.societyId ?? 0,
          workflowId: workflowId,
        },
        body: {
          payloadValue: {
            first_name: values.firstName,
            last_name: values.lastName,
            email: values.email,
            affiliation: values.affiliation,
            profile_type: profile_type.Society_User,
          },
          eventName: "Submit",
        },
      },
    });

    if (response.profileId) {
      const newProfile = {
        profile: {
          profileId: response.profileId,
          displayName: `${response.payload?.first_name} ${response.payload?.last_name}`,
          affiliation: response.payload?.affiliation,
          email: response.payload?.email,
        },
      };
      handleChanged(newProfile);
      setOverrideAffiliationValue(response.payload?.affiliation);
      setCreateProfile(false);
      setNewValue(null);
      setIsLocked(true);
    }
  };

  const getAlreadySelectedProfileIds = (): Set<number> => {
    if (!survey) {
      return new Set();
    }

    const selectedIds = new Set<number>();

    const allQuestions = survey.getAllQuestions();
    const panelDynamicQuestion = allQuestions.find(
      (q) => q.getType() === ProfileSearchMultipleQuestionType,
    );

    if (!panelDynamicQuestion) {
      return selectedIds;
    }

    const preventMultipleAssignment =
      (panelDynamicQuestion as any).getPropertyValue?.(
        ProfileSearchMultipleQuestionProperties.profileSearchQuestionProperties
          .preventMultipleAssignment,
      ) || false;

    if (!preventMultipleAssignment) {
      return selectedIds;
    }

    const allValues: ProfileSearchMultipleQuestionData[] =
      (panelDynamicQuestion as any).value || [];

    allValues.forEach((item) => {
      if (item.profilesearch?.profileId) {
        selectedIds.add(item.profilesearch.profileId);
      }
    });

    return selectedIds;
  };

  const handleInlineProfileSelect = (
    label: string,
    affiliation?: string,
    email?: string,
  ) => {
    const displayName = label;

    const inlineOption: OptionsValuesSurveyJsProfileSearch = {
      value: {
        profileId: -2,
        displayName,
        affiliation: affiliation ?? "",
        email: email ?? "",
      },
      label: displayName,
    };

    setNewValue(inlineOption);
    setOverrideAffiliationValue(affiliation ?? "");
    handleChanged({
      profile: {
        profileId: -2,
        displayName,
        affiliation: affiliation ?? "",
        email: email ?? "",
      },
    });
    setIsLocked(true);
  };

  const fetchDirectoryProfiles = useCallback(
    async (
      inputValue: string,
      loadedOptions: OptionsOrGroups<
        OptionsValuesSurveyJsProfileSearch,
        GroupBase<OptionsValuesSurveyJsProfileSearch>
      >,
      page = 1,
    ) => {
      const pageSize = 25;
      const directoryResponse = await apiRequest({
        contract: getSocietyDirectoryIdContract,
        inputs: {
          params: {
            societyId: Number(session?.societyId) ?? "",
            directoryId: directoryId ?? "",
          },
        },
        filters: {
          text: inputValue,
          ...apiFilters,
        },
        pagination: {
          page,
          pageSize,
        },
      });

      const options: OptionsValuesSurveyJsProfileSearch[] =
        directoryResponse.results.map((profile) => ({
          value: {
            profileId: profile.profileId,
            displayName: getDisplayNameHelper(profile),
            affiliation: profile.societyUser?.affiliation ?? "",
            email: defaultEmailFromProfile(profile) ?? "",
          },
          label: getDisplayNameHelper(profile, true),
        }));

      const selectedIds = getAlreadySelectedProfileIds();
      const filteredOptions = options.filter(
        (option) => !selectedIds.has(option.value.profileId),
      );

      const hasMore =
        directoryResponse.page <
        Math.ceil(directoryResponse.totalResults / pageSize);

      const inlineOption =
        inputValue && allowInlineParticipantEntry
          ? [
              {
                value: {
                  profileId: -2,
                },
                label: inputValue,
              },
            ]
          : [];

      const resultOptions =
        Number(workflowId) === -1
          ? [...inlineOption, ...filteredOptions]
          : [...inlineOption, createNewProfileOption, ...filteredOptions];

      return {
        options: resultOptions,
        hasMore,
        additional: page + 1,
      };
    },
    [directoryId, session, workflowId, apiFilters, survey],
  );

  const handleRemoveSelection = () => {
    setNewValue(null);
    setOverrideAffiliationValue("");
    setIsLocked(false);
    setHasExistingProfile(false);
    handleChanged({ profile: null });
  };

  const isAffilationVisible = newValue?.value?.profileId !== -1 && newValue;

  return (
    <div>
      <PillarForm
        className={survey?.isDesignMode ? "mb-7" : "mb-3"}
        handleSubmit={handleCreateProfile}
        handleChanged={(values) => {
          if (existingValue) {
            setHasExistingProfile(true);
            handleChanged({
              profile: {
                profileId: existingValue.profileId,
                displayName: existingValue.displayName,
                affiliation: existingValue.affiliation,
                email: existingValue.email,
                workflowInstanceId: existingValue.workflowInstanceId,
              },
            });
          } else {
            handleChanged(values);
          }
        }}
        yupValidation={createProfile ? validationSchema : Yup.object()}
      >
        <div
          data-testid={`profilesearchmultiple_input_${panelIndex}`}
          data-name="profile_id"
          className="w-full flex items-end gap-x-3"
        >
          <div className="w-full relative">
            {allowChangeProfile && newValue && isLocked && (
              <div className="absolute top-1/2 right-1.5 z-[2] flex items-center gap-x-2 -translate-y-1/2">
                <PillarForm.Button
                  testid="remove-selection-btn"
                  onClick={handleRemoveSelection}
                  className="p-0 w-3 h-3 button-regular-destructive-filled flex items-center justify-center rounded-full border border-neutral-light"
                  icon={<CrossIcon className="h-2 w-2" />}
                />
              </div>
            )}
            <AsyncPaginate<
              OptionsValuesSurveyJsProfileSearch,
              GroupBase<OptionsValuesSurveyJsProfileSearch>,
              number
            >
              key={selectKey}
              className={`w-full select ${isLocked ? "locked-select" : ""}`}
              classNamePrefix="select"
              loadOptions={fetchDirectoryProfiles}
              isDisabled={
                (!session?.adminMode &&
                  (createProfile || hasExistingProfile)) ||
                isLocked
              }
              debounceTimeout={500}
              placeholder="Search for a profile"
              value={newValue}
              formatOptionLabel={(
                option: OptionsValuesSurveyJsProfileSearch,
              ) => {
                if (!option || option.value.profileId === -1) {
                  return option?.label ?? "";
                }
                return (
                  <div className="flex flex-col">
                    <div className="">{option.label}</div>
                    {option.value.email && (
                      <div
                        className={`mt-0.5 text-sm text-neutral-${
                          isLocked ? "light" : "mid-500"
                        }`}
                      >
                        {option.value.email}
                      </div>
                    )}
                  </div>
                );
              }}
              onChange={(
                newValue: SingleValue<OptionsValuesSurveyJsProfileSearch>,
                actionMeta: ActionMeta<OptionsValuesSurveyJsProfileSearch>,
              ) => {
                const { profileId, displayName, affiliation, email } =
                  newValue?.value || {};
                if (profileId === -2) {
                  handleInlineProfileSelect(
                    newValue?.label ?? "",
                    affiliation,
                    email,
                  );
                } else if (profileId === -1) {
                  setCreateProfile(true);
                  setNewValue(createNewProfileOption);
                  setIsLocked(false);
                } else {
                  setNewValue(newValue);
                  handleChanged({
                    profile: {
                      profileId: profileId ?? 0,
                      displayName: displayName ?? "",
                      affiliation: affiliation ?? "",
                      email: email ?? "",
                      workflowInstanceId:
                        preservedWorkflowInstanceIdRef.current,
                    },
                  });
                  setOverrideAffiliationValue(affiliation ?? "");
                  setIsLocked(true);
                }
              }}
              additional={1}
            />
          </div>

          {overrideAffiliation && isAffilationVisible && (
            <label
              htmlFor="override_affiliation"
              className="flex flex-col min-w-5xl"
            >
              <input
                data-testid="override-affiliation-input"
                id="override_affiliation"
                type="text"
                name="override_affiliation"
                className="sd-input sd-text"
                value={overrideAffiliationValue}
                placeholder="Add Affiliation"
                disabled={!allowChangeProfile}
                onChange={(e) => {
                  if (!newValue) {
                    return;
                  }
                  setOverrideAffiliationValue(e.target.value);
                  handleChanged({
                    profile: {
                      ...newValue?.value,
                      affiliation: e.target.value,
                    },
                  });
                }}
              />
            </label>
          )}
        </div>
        {createProfile && (
          <ul className="pt-2 pb-3">
            <div className="grid grid-cols-2 gap-x-1 gap-y-2">
              <PillarForm.TextInput name="firstName" label="First Name" />
              <PillarForm.TextInput name="lastName" label="Last Name" />
              <PillarForm.TextInput name="email" label="Email" />
              <PillarForm.TextInput name="affiliation" label="Affiliation" />
            </div>
            <div className="flex justify-start gap-x-2 mt-2">
              <PillarForm.LoadingSubmitButton className="bg-primary-400 hover:bg-primary-600 text-white mt-1 ">
                Create
              </PillarForm.LoadingSubmitButton>
              <PillarForm.Button
                onClick={() => setCreateProfile(false)}
                className="bg-status-danger hover:bg-status-danger-tint text-white mt-1"
              >
                Cancel
              </PillarForm.Button>
            </div>
          </ul>
        )}
      </PillarForm>
    </div>
  );
};

export default SurveyJSProfileSearch;
